// PRODUCTION — admin flow editor grouped step list
import React, { Fragment, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { useFlowEditorContext } from 'src/pages/admin/flow-editor/context/useFlowEditorContext'
import { FlowEditorStepGroupStepRow } from './FlowEditorStepGroupStepRow'
import { FlowEditorStepGroupTrailingDropTarget } from './FlowEditorStepGroupTrailingDropTarget'
import { FlowEditorStepDropIndicator } from './FlowEditorStepDropIndicator'
import { normalizeCollectDataTemplateConfigId } from 'src/utils/collectDataTemplateEditor/configIds'

export function FlowEditorStepGroupSidebar() {
  const {
    draggedStepConfigId,
    draggedStepId,
    flowEditorSidebarGroups,
    handleAddEmptyStepGroup,
    handleDragEnd,
    handleDragOver,
    handleDragStart,
    handleDrop,
    handleRemoveStep,
    handleSelectGroup,
    isDropTarget,
    openAddStepModal,
    pendingDeleteId,
    selectedGroupConfigId,
    selectedStepId,
    setPendingDeleteId,
    setSelectedGroupConfigId,
    setSelectedStepId,
    unsavedStepIds,
  } = useFlowEditorContext()

  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(() => new Set())

  const toggleGroupCollapsed = useCallback((configId: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev)

      if (next.has(configId)) {
        next.delete(configId)
      } else {
        next.add(configId)
      }

      return next
    })
  }, [])

  return (
    <div
      css={tw`flex min-h-0 w-[332px] flex-shrink-0 flex-col overflow-hidden rounded-xl border border-shades-200 bg-shades-50 border-solid`}
    >
      <div
        css={tw`box-border flex min-h-[61px] flex-shrink-0 items-center justify-between border-0 border-b border-solid border-shades-100 bg-shades-0 py-3.5 pl-4 pr-3`}
      >
        <div css={tw`flex items-center gap-2`}>
          <span css={tw`text-[15px] font-semibold text-shades-900`}>Step groups</span>
          <span css={tw`rounded-[10px] bg-shades-100 px-2 text-[11px] font-bold text-shades-600`}>
            {flowEditorSidebarGroups.length}
          </span>
        </div>
      </div>

      <div
        css={tw`flex min-h-0 flex-1 flex-col items-stretch gap-3 overflow-y-auto overflow-x-hidden p-4`}
      >
        {flowEditorSidebarGroups.length === 0 ? (
          <div css={tw`px-3 py-6 text-center text-[13px] text-shades-400`}>No step groups yet</div>
        ) : (
          flowEditorSidebarGroups.map((group) => {
            const groupHasSelection =
              selectedGroupConfigId === group.configId ||
              (selectedStepId !== null && group.groupSteps.some((s) => s.id === selectedStepId))
            const collapsed = collapsedGroupIds.has(group.configId)
            const groupCardSelected = selectedGroupConfigId === group.configId
            const normalizedGroupConfigId =
              normalizeCollectDataTemplateConfigId(group.configId) || group.configId
            const isDragSourceGroup =
              draggedStepConfigId !== null &&
              (normalizeCollectDataTemplateConfigId(draggedStepConfigId) || draggedStepConfigId) ===
                normalizedGroupConfigId

            return (
              <div
                key={group.configId}
                css={[
                  tw`box-border flex min-h-min flex-shrink-0 flex-row overflow-hidden rounded-[10px]`,
                  groupCardSelected
                    ? tw`border border-primary-300 bg-primary-50 border-solid`
                    : tw`border border-shades-200 bg-shades-0 border-solid`,
                ]}
              >
                <div
                  aria-hidden
                  css={[
                    tw`w-1 flex-shrink-0 self-stretch`,
                    groupHasSelection ? tw`bg-primary-500` : tw`bg-transparent`,
                  ]}
                />
                <div css={tw`flex min-h-min min-w-0 flex-1 flex-col`}>
                  <div
                    css={tw`flex items-center gap-2 border-0 border-b border-solid border-shades-100 py-2.5 pl-2.5 pr-3`}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      css={tw`flex min-w-0 flex-1 cursor-pointer items-center gap-2`}
                      onClick={() => handleSelectGroup(group.configId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSelectGroup(group.configId)
                        }
                      }}
                    >
                      <span
                        css={tw`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-shades-100 text-[11px] font-bold text-shades-700`}
                      >
                        {group.groupOrdinal}
                      </span>
                      <div css={tw`min-w-0 flex-1`}>
                        <div
                          css={tw`overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-semibold text-shades-900`}
                        >
                          {group.displayLabel}
                        </div>
                      </div>
                      {group.displayDelayed ? (
                        <span
                          css={tw`flex-shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-primary-700 bg-primary-100`}
                        >
                          DELAYED
                        </span>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      aria-expanded={!collapsed}
                      aria-label={collapsed ? 'Expand step group' : 'Collapse step group'}
                      onClick={(e) => {
                        e.stopPropagation()
                        toggleGroupCollapsed(group.configId)
                      }}
                      css={tw`flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-shades-600 hover:bg-shades-100 border-solid`}
                    >
                      {collapsed ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      ) : (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {!collapsed && group.isEmptyPlaceholder && group.groupSteps.length === 0 ? (
                    <div css={tw`px-3 pb-3 pt-2.5 text-xs text-shades-400`}>
                      No steps in this group yet
                    </div>
                  ) : null}

                  {!collapsed
                    ? group.groupSteps.map((step, stepIndexInGroup) => {
                        const insertDropTarget = {
                          configId: group.configId,
                          indexInGroup: stepIndexInGroup,
                        }
                        const showInsertIndicator =
                          isDragSourceGroup && isDropTarget(insertDropTarget)

                        return (
                          <Fragment key={`${group.configId}-${step.id}`}>
                            {showInsertIndicator ? <FlowEditorStepDropIndicator /> : null}
                            <FlowEditorStepGroupStepRow
                              groupConfigId={group.configId}
                              draggedStepConfigId={draggedStepConfigId}
                              draggedStepId={draggedStepId}
                              handleDragEnd={handleDragEnd}
                              handleDragOver={handleDragOver}
                              handleDragStart={handleDragStart}
                              handleDrop={handleDrop}
                              handleRemoveStep={handleRemoveStep}
                              pendingDeleteId={pendingDeleteId}
                              selectedStepId={selectedStepId}
                              setPendingDeleteId={setPendingDeleteId}
                              setSelectedGroupConfigId={setSelectedGroupConfigId}
                              setSelectedStepId={setSelectedStepId}
                              step={step}
                              stepIndexInGroup={stepIndexInGroup}
                              unsavedStepIds={unsavedStepIds}
                            />
                          </Fragment>
                        )
                      })
                    : null}

                  {!collapsed && isDragSourceGroup ? (
                    <FlowEditorStepGroupTrailingDropTarget
                      configId={group.configId}
                      draggedStepId={draggedStepId}
                      groupStepCount={group.groupSteps.length}
                      handleDragEnd={handleDragEnd}
                      handleDragOver={handleDragOver}
                      handleDrop={handleDrop}
                      isDropTarget={isDropTarget}
                    />
                  ) : null}

                  <div css={tw`px-3 pb-2.5 pt-1`}>
                    <Button
                      size="tiny"
                      color="light"
                      disabled={group.addStepDisabled}
                      title={
                        group.addStepDisabled
                          ? 'Insurance policy groups with synced coverage steps cannot include other step types'
                          : undefined
                      }
                      onClick={() => openAddStepModal(group.configId)}
                      className={tw`w-[108px]`}
                    >
                      Add step
                    </Button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      <div css={tw`flex-shrink-0 border-0 border-t border-solid border-shades-100 bg-shades-0 p-3`}>
        <Button
          fullWidth
          size="small"
          onClick={handleAddEmptyStepGroup}
          leftIcon={
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth={3}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          }
        >
          Add step config
        </Button>
      </div>
    </div>
  )
}
